import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Info } from "lucide-react";
import { api, rp } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";
const lbl = "text-[11px] uppercase tracking-widest text-ink-soft";
const MODES = [
  ["pickup", "Pickup / diambil customer"],
  ["customer_arranged_delivery", "Delivery (kurir customer, Rp0)"],
  ["play_at_venue", "Main di Venue"],
];

const blank = (date) => ({
  branch_id: "", service_mode: "pickup", pricing_mode: "package", package_id: "",
  price_override: "", custom_total: "", custom_label: "", duration_hours: 4,
  rental_date: date, start_time: "10:00", quantity: 1, extra_hours: 0,
  unit_mode: "auto", unit_ids: [], addon_ids: [],
  customer_name: "", customer_whatsapp: "", customer_email: "",
  deposit_amount: "", manual_discount: "", manual_discount_reason: "", delivery_fee: "",
  notes: "", internal_notes: "", override_rules: false, override_reason: "",
});

const preferredHub = (branches, wanted) =>
  wanted || branches.find((b) => (b.units_total || 0) > 0)?.id || branches[0]?.id || "";

export const ManualBookingForm = ({ open, onOpenChange, branches, defaultDate, defaultBranchId,
  isOwner, hold, onSaved }) => {
  const [f, setF] = useState(blank(defaultDate));
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));

  useEffect(() => {
    if (!open) return;
    setF({
      ...blank(hold?.rental_date || defaultDate),
      branch_id: hold?.branch_id || preferredHub(branches, defaultBranchId),
      start_time: hold?.start_time || "10:00",
      quantity: hold?.quantity || 1,
      customer_name: hold?.contact_name || "",
      customer_whatsapp: hold?.contact_whatsapp || "",
    });
    setQ(null);
    Promise.all([api.get("/packages"), api.get("/addons")])
      .then(([p, a]) => {
        const list = (p.data || []).filter((x) => x.active);
        setPackages(list);
        setAddons((a.data || []).filter((x) => x.active));
        setF((prev) => ({ ...prev, package_id: prev.package_id || list[0]?.id || "" }));
      }).catch(() => {});
  }, [open, defaultDate, defaultBranchId, branches, hold]);

  const body = useCallback(() => ({
    branch_id: f.branch_id || undefined, service_mode: f.service_mode,
    pricing_mode: f.pricing_mode,
    package_id: f.pricing_mode === "package" ? f.package_id : undefined,
    price_override: f.pricing_mode === "package" && f.price_override !== "" ? Number(f.price_override) : undefined,
    custom_total: f.pricing_mode === "custom_event" ? Number(f.custom_total || 0) : undefined,
    custom_label: f.custom_label || undefined,
    duration_hours: f.pricing_mode === "custom_event" ? Number(f.duration_hours || 0) : undefined,
    rental_date: f.rental_date, start_time: f.start_time,
    quantity: f.unit_mode === "manual" ? f.unit_ids.length || 1 : Number(f.quantity || 1),
    unit_ids: f.unit_mode === "manual" ? f.unit_ids : undefined,
    extra_hours: Number(f.extra_hours || 0), addon_ids: f.addon_ids,
    deposit_amount: f.deposit_amount === "" ? undefined : Number(f.deposit_amount),
    manual_discount: Number(f.manual_discount || 0),
    manual_discount_reason: f.manual_discount_reason,
    delivery_fee: f.delivery_fee === "" ? undefined : Number(f.delivery_fee),
    customer_name: f.customer_name, customer_whatsapp: f.customer_whatsapp,
    customer_email: f.customer_email, notes: f.notes, internal_notes: f.internal_notes,
    override_rules: f.override_rules, override_reason: f.override_reason,
    hold_id: hold?.id || undefined,
  }), [f, hold]);

  useEffect(() => {
    if (!open) return;
    const ready = f.rental_date && (f.pricing_mode === "package"
      ? f.package_id
      : Number(f.custom_total) > 0 && Number(f.duration_hours) > 0);
    if (!ready) { setQ(null); return; }
    const t = setTimeout(() => {
      api.post("/bookings/manual/quote", body())
        .then((r) => setQ(r.data))
        .catch((e) => setQ({ error: e?.response?.data?.detail || "Gagal menghitung kuotasi" }));
    }, 450);
    return () => clearTimeout(t);
  }, [open, body, f.rental_date, f.package_id, f.pricing_mode, f.custom_total, f.duration_hours]);

  const toggle = (key, id) => set({
    [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
  });

  const save = async () => {
    if (!f.customer_name.trim() || f.customer_whatsapp.trim().length < 8) {
      toast.error("Nama dan WhatsApp customer wajib diisi"); return;
    }
    setBusy(true);
    try {
      const r = await api.post("/bookings/manual", body());
      toast.success(`${r.data.booking_code} dibuat — ${rp(r.data.total_payment)}`);
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat booking manual");
    } finally { setBusy(false); }
  };

  const rows = q && !q.error ? [
    ["Sewa", q.rental_subtotal], ["Add-on", q.addon_total], ["Jam tambahan", q.extra_hours_total],
    ["Biaya kurir", q.delivery_fee], ["Diskon promo", -(q.promo_discount || 0)],
    ["Diskon member", -(q.loyalty_discount || 0)], ["Diskon manual", -(q.manual_discount || 0)],
  ].filter(([, v]) => v) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{hold ? `Jadikan booking — ${hold.title}` : "Booking manual"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2" data-testid="manual-booking-form">
          <label className="block"><span className={lbl}>Hub</span>
            <select className={`${sel} mt-1`} data-testid="mb-branch" value={f.branch_id}
              onChange={(e) => set({ branch_id: e.target.value, unit_ids: [] })}>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
          <label className="block"><span className={lbl}>Mode layanan</span>
            <select className={`${sel} mt-1`} data-testid="mb-service-mode" value={f.service_mode}
              onChange={(e) => set({ service_mode: e.target.value })}>
              {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>

          <div className="sm:col-span-2 flex flex-wrap gap-2">
            {[["package", "Harga paket"], ["custom_event", "Harga custom (acara)"]].map(([v, l]) => (
              <button key={v} data-testid={`mb-pricing-${v}`} disabled={v === "custom_event" && !isOwner}
                onClick={() => set({ pricing_mode: v })}
                className={`rounded-full border px-4 py-2 text-xs font-semibold disabled:opacity-40 ${
                  f.pricing_mode === v ? "border-ink bg-ink text-cream" : "border-black/15 hover:border-ink"}`}>
                {l}
              </button>
            ))}
            {!isOwner && <span className="self-center text-[11px] text-ink-soft">Harga custom hanya untuk Owner</span>}
          </div>

          {f.pricing_mode === "package" ? (
            <>
              <label className="block"><span className={lbl}>Paket</span>
                <select className={`${sel} mt-1`} data-testid="mb-package" value={f.package_id}
                  onChange={(e) => set({ package_id: e.target.value })}>
                  {packages.map((p) => <option key={p.id} value={p.id}>{p.name} — {rp(p.normal_price)}</option>)}
                </select>
              </label>
              {isOwner && (
                <label className="block"><span className={lbl}>Harga per meja (override)</span>
                  <Input type="number" className="mt-1" data-testid="mb-price-override" placeholder="Kosong = harga paket"
                    value={f.price_override} onChange={(e) => set({ price_override: e.target.value })} />
                </label>
              )}
            </>
          ) : (
            <>
              <label className="block"><span className={lbl}>Total harga (Rupiah, seluruh booking)</span>
                <Input type="number" className="mt-1" data-testid="mb-custom-total" value={f.custom_total}
                  onChange={(e) => set({ custom_total: e.target.value })} />
              </label>
              <label className="block"><span className={lbl}>Durasi acara (jam)</span>
                <Input type="number" min="1" step="0.5" className="mt-1" data-testid="mb-duration"
                  value={f.duration_hours} onChange={(e) => set({ duration_hours: e.target.value })} />
              </label>
              <label className="sm:col-span-2 block"><span className={lbl}>Nama kuotasi</span>
                <Input className="mt-1" data-testid="mb-custom-label" placeholder="Mis. Turnamen Kantor 8 Jam"
                  value={f.custom_label} onChange={(e) => set({ custom_label: e.target.value })} />
              </label>
            </>
          )}

          <label className="block"><span className={lbl}>Tanggal</span>
            <Input type="date" className="mt-1" data-testid="mb-date" value={f.rental_date}
              onChange={(e) => set({ rental_date: e.target.value })} />
          </label>
          <label className="block"><span className={lbl}>Jam mulai</span>
            <Input type="time" className="mt-1" data-testid="mb-start-time" value={f.start_time}
              onChange={(e) => set({ start_time: e.target.value })} />
          </label>

          <label className="block"><span className={lbl}>Pilih meja</span>
            <select className={`${sel} mt-1`} data-testid="mb-unit-mode" value={f.unit_mode}
              onChange={(e) => set({ unit_mode: e.target.value })}>
              <option value="auto">Otomatis</option>
              <option value="manual">Pilih meja sendiri</option>
            </select>
          </label>
          {f.unit_mode === "auto" ? (
            <label className="block"><span className={lbl}>Jumlah meja</span>
              <Input type="number" min="1" className="mt-1" data-testid="mb-quantity" value={f.quantity}
                onChange={(e) => set({ quantity: e.target.value })} />
            </label>
          ) : (
            <div className="sm:col-span-2">
              <span className={lbl}>Meja ({f.unit_ids.length} dipilih)</span>
              <div className="mt-1 flex flex-wrap gap-2">
                {(q?.units || []).map((u) => (
                  <button key={u.id} data-testid={`mb-unit-${u.unit_code}`} disabled={!u.free && !f.unit_ids.includes(u.id)}
                    onClick={() => toggle("unit_ids", u.id)}
                    className={`rounded-md border px-3 py-1.5 text-xs disabled:opacity-35 ${
                      f.unit_ids.includes(u.id) ? "border-ink bg-ink text-cream" : "border-black/15 hover:border-ink"}`}>
                    {u.unit_code}{u.free ? "" : " · terpakai"}
                  </button>
                ))}
                {!(q?.units || []).length && <span className="text-xs text-ink-soft">Isi tanggal & paket dulu</span>}
              </div>
            </div>
          )}

          <label className="block"><span className={lbl}>Jam tambahan</span>
            <Input type="number" min="0" max="24" className="mt-1" data-testid="mb-extra-hours"
              value={f.extra_hours} onChange={(e) => set({ extra_hours: e.target.value })} />
          </label>
          <label className="block"><span className={lbl}>Deposit (kosong = aturan default)</span>
            <Input type="number" className="mt-1" data-testid="mb-deposit" value={f.deposit_amount}
              onChange={(e) => set({ deposit_amount: e.target.value })} />
          </label>

          {addons.length > 0 && (
            <div className="sm:col-span-2">
              <span className={lbl}>Add-on</span>
              <div className="mt-1 flex flex-wrap gap-2">
                {addons.map((a) => (
                  <button key={a.id} data-testid={`mb-addon-${a.id}`} onClick={() => toggle("addon_ids", a.id)}
                    className={`rounded-md border px-3 py-1.5 text-xs ${f.addon_ids.includes(a.id)
                      ? "border-ink bg-ink text-cream" : "border-black/15 hover:border-ink"}`}>
                    {a.name} · {rp(a.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="block"><span className={lbl}>Nama customer</span>
            <Input className="mt-1" data-testid="mb-customer-name" value={f.customer_name}
              onChange={(e) => set({ customer_name: e.target.value })} />
          </label>
          <label className="block"><span className={lbl}>WhatsApp customer</span>
            <Input className="mt-1" data-testid="mb-customer-wa" placeholder="0812xxxxxxx"
              value={f.customer_whatsapp} onChange={(e) => set({ customer_whatsapp: e.target.value })} />
          </label>
          <label className="block"><span className={lbl}>Diskon manual</span>
            <Input type="number" className="mt-1" data-testid="mb-discount" value={f.manual_discount}
              onChange={(e) => set({ manual_discount: e.target.value })} />
          </label>
          <label className="block"><span className={lbl}>Alasan diskon</span>
            <Input className="mt-1" data-testid="mb-discount-reason" value={f.manual_discount_reason}
              onChange={(e) => set({ manual_discount_reason: e.target.value })} />
          </label>
          <label className="sm:col-span-2 block"><span className={lbl}>Catatan</span>
            <Textarea rows={2} className="mt-1" data-testid="mb-notes" value={f.notes}
              onChange={(e) => set({ notes: e.target.value })} />
          </label>

          <div className="sm:col-span-2 rounded-lg border border-black/10 bg-cream-dark/50 p-3" data-testid="mb-quote">
            {q?.error && <div className="text-xs text-red-700">{q.error}</div>}
            {q && !q.error && (
              <>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-xs text-ink-soft">
                    {q.package_name_snapshot} · {q.quantity} meja · {q.available_units} meja bebas
                  </span>
                  <span className="font-display text-2xl">{rp(q.total_payment)}</span>
                </div>
                <div className="mt-2 space-y-0.5 text-[11px] text-ink-soft">
                  {rows.map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span>{k}</span><span>{rp(v)}</span></div>
                  ))}
                  <div className="flex justify-between font-semibold text-ink">
                    <span>Total sewa</span><span>{rp(q.final_rental_total)}</span>
                  </div>
                  <div className="flex justify-between"><span>Deposit</span><span>{rp(q.deposit_amount)}</span></div>
                </div>
                {(q.hub_warnings || []).map((w) => (
                  <div key={w} className="mt-2 flex gap-2 text-[11px] text-amber-800">
                    <Info size={13} className="mt-0.5 shrink-0" />{w}
                  </div>
                ))}
                {(q.hub_errors || []).map((w) => (
                  <div key={w} className="mt-2 flex gap-2 text-[11px] text-red-700" data-testid="mb-hub-error">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />{w}
                  </div>
                ))}
              </>
            )}
            {!q && <div className="text-xs text-ink-soft">Isi tanggal, paket / harga custom untuk melihat kuotasi.</div>}
          </div>

          {q && !q.error && (q.hub_errors || []).length > 0 && (
            <div className="sm:col-span-2 rounded-lg border border-red-300 bg-red-50 p-3">
              <label className="flex items-center gap-2">
                <input type="checkbox" data-testid="mb-override" checked={f.override_rules}
                  onChange={(e) => set({ override_rules: e.target.checked })} className="h-4 w-4" />
                <span className="text-xs font-semibold text-red-800">Lewati aturan Hub untuk booking ini</span>
              </label>
              {f.override_rules && (
                <Input className="mt-2 bg-white" data-testid="mb-override-reason" value={f.override_reason}
                  placeholder="Alasan (masuk audit log)" onChange={(e) => set({ override_reason: e.target.value })} />
              )}
            </div>
          )}

          <div className="sm:col-span-2 flex flex-wrap gap-2">
            <button data-testid="mb-save" disabled={busy || !q || q.error} onClick={save}
              className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
              {busy ? "Menyimpan…" : "Buat booking manual"}
            </button>
            <button onClick={() => onOpenChange(false)} className="rounded-md border border-black/15 px-5 py-2.5 text-sm">
              Batal
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
